import { MAX_COMMENT_ATTACHMENTS, MAX_COMMENT_LINKS } from '@atlas/models'
import { FileThumbnail } from '@atlas/ui-components'
import type { ICommentLink } from '@atlas/types'
import { Button, buttonVariants, Chip, cn, HelpText, Input, Spinner } from '@atlas/ui-primitives'
import { ImagePlus, Link2, X } from 'lucide-react'
import { type ChangeEvent, type FormEvent, type KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react'
import { notification } from '@/notifications/notification-store'
import type { CommentInput } from '@/services/comment.service'

const IMAGE_TYPES = ['image/png', 'image/jpeg']
const MAX_IMAGE_BYTES = 50 * 1024 * 1024

/** A picked image: previewed locally at once, attachable once its upload has a `fileId`. */
interface PendingImage {
	key: string
	name: string
	previewUrl: string
	fileId?: string
}

/** The server refuses anything else; checking here only spares the round trip. */
export const isHttpUrl = (value: string) => /^https?:$/.test(URL.parse(value)?.protocol ?? '')

export interface CommentComposerProps {
	/** The draft is cleared once this resolves, and kept for a retry if it rejects. */
	onComment: (input: CommentInput) => Promise<unknown> | undefined
	isPosting?: boolean
	/** Uploads one reference image and resolves with its `fileId`. Absent hides the attach action. */
	onUploadImage?: (file: File) => Promise<string>
	/** Deletes an uploaded image the draft no longer holds: removed, or abandoned with the composer. */
	onDiscardImage?: (fileId: string) => void
}

/** Adds a link: a URL plus an optional label, refused unless it is http(s). */
function LinkForm({ onAdd, onCancel }: { onAdd: (link: ICommentLink) => void; onCancel: () => void }) {
	const [url, setUrl] = useState('')
	const [label, setLabel] = useState('')
	const [invalid, setInvalid] = useState(false)
	const add = () => {
		const trimmed = url.trim()
		if (!isHttpUrl(trimmed)) return setInvalid(true)
		onAdd(label.trim() ? { url: trimmed, label: label.trim() } : { url: trimmed })
	}
	// The fields sit inside the comment form, where Enter would post the comment instead.
	const addOnEnter = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key !== 'Enter') return
		e.preventDefault()
		add()
	}
	return (
		<div className="space-y-1">
			<div className="flex items-center gap-2">
				<Input
					aria-label="Link URL"
					value={url}
					onChange={(e: ChangeEvent<HTMLInputElement>) => {
						setUrl(e.target.value)
						setInvalid(false)
					}}
					onKeyDown={addOnEnter}
					placeholder="https://…"
					className="flex-1"
					aria-invalid={invalid}
				/>
				<Input
					aria-label="Link label"
					value={label}
					onChange={(e: ChangeEvent<HTMLInputElement>) => setLabel(e.target.value)}
					onKeyDown={addOnEnter}
					placeholder="Label (optional)"
					className="w-1/3"
				/>
				<Button size="sm" variant="secondary" onPress={add}>
					Add
				</Button>
				<Button size="sm" variant="ghost" onPress={onCancel}>
					Cancel
				</Button>
			</div>
			{invalid ? <HelpText variant="error">The link must start with http:// or https://</HelpText> : null}
		</div>
	)
}

/** A picked image's tile: its local preview, a spinner while it uploads, and a remove control. */
function PendingImageTile({ image, onRemove }: { image: PendingImage; onRemove: () => void }) {
	return (
		<li className="relative">
			<FileThumbnail name={image.name} thumbnailUrl={image.previewUrl} size="md" />
			{image.fileId ? null : (
				<span className="absolute inset-0 flex items-center justify-center bg-ds-surface/60">
					<Spinner size="sm" label={`Uploading ${image.name}`} />
				</span>
			)}
			<button
				type="button"
				aria-label={`Remove ${image.name}`}
				onClick={onRemove}
				className="absolute -right-1.5 -top-1.5 rounded-ds-full border border-ds-border bg-ds-surface-elevated p-0.5 text-ds-text-subtle hover:text-ds-danger-strong"
			>
				<X className="h-3 w-3" />
			</button>
		</li>
	)
}

/**
 * Picked images: previewed locally at once, uploaded straight away, and dropped
 * with an error toast naming them when they are too big, past the per-comment
 * cap, or fail to upload.
 *
 * An uploaded image the draft lets go of — removed, or still held when the
 * composer unmounts — is handed to `onDiscardImage`, including one whose upload
 * was still running at the time, so no reference file outlives its draft.
 * An image stops being draft the moment it is posted: leaving mid-post must not
 * delete what the new comment points to.
 */
function usePendingImages({
	onUploadImage,
	onDiscardImage,
}: Pick<CommentComposerProps, 'onUploadImage' | 'onDiscardImage'>) {
	const [images, setImages] = useState<PendingImage[]>([])
	const dropped = useRef(new Set<string>())
	const sent = useRef(new Set<string>())
	const latest = useRef({ images, onDiscardImage })
	latest.current = { images, onDiscardImage }

	const forget = (keys: Set<string>) =>
		setImages((list) =>
			list.filter((image) => {
				if (keys.has(image.key)) URL.revokeObjectURL(image.previewUrl)
				return !keys.has(image.key)
			}),
		)

	/** Lets go of an image: discards its upload now, or once it lands. */
	const release = useCallback((image: PendingImage) => {
		if (sent.current.has(image.key)) return
		if (image.fileId) latest.current.onDiscardImage?.(image.fileId)
		else dropped.current.add(image.key)
	}, [])

	const removeImage = (key: string) => {
		const image = images.find((candidate) => candidate.key === key)
		if (image) release(image)
		forget(new Set([key]))
	}

	useEffect(() => () => latest.current.images.forEach(release), [release])

	const pickImages = (e: ChangeEvent<HTMLInputElement>) => {
		// `accept` is only a hint to the picker, so the type is checked here too.
		const files = Array.from(e.target.files ?? []).filter((file) => {
			if (IMAGE_TYPES.includes(file.type)) return true
			notification.error(`${file.name} is not a PNG or JPG.`)
			return false
		})
		// Cleared so picking the same file again still fires a change.
		e.target.value = ''
		const room = MAX_COMMENT_ATTACHMENTS - images.length
		if (files.length > room) notification.error(`At most ${MAX_COMMENT_ATTACHMENTS} images per comment.`)
		for (const file of files.slice(0, Math.max(room, 0))) {
			if (file.size > MAX_IMAGE_BYTES) {
				notification.error(`${file.name} is over 50MB.`)
				continue
			}
			const key = crypto.randomUUID()
			setImages((list) => [...list, { key, name: file.name, previewUrl: URL.createObjectURL(file) }])
			onUploadImage?.(file)
				.then((fileId) => {
					if (dropped.current.delete(key)) return latest.current.onDiscardImage?.(fileId)
					setImages((list) => list.map((image) => (image.key === key ? { ...image, fileId } : image)))
				})
				.catch(() => {
					notification.error(`${file.name} could not be uploaded.`)
					dropped.current.delete(key)
					forget(new Set([key]))
				})
		}
	}

	/** Hands images to a post: kept from discard while it runs, dropped from the draft once it lands. */
	const postImages = (posted: PendingImage[], request: Promise<unknown>) => {
		for (const image of posted) sent.current.add(image.key)
		return request.then(
			() => forget(new Set(posted.map((image) => image.key))),
			(error: unknown) => {
				for (const image of posted) sent.current.delete(image.key)
				throw error
			},
		)
	}

	return { images, pickImages, removeImage, postImages }
}

/**
 * Bottom-anchored composer — you type under the last comment (chat-style).
 *
 * Images upload the moment they are picked, so posting is instant; the Comment
 * button waits until every picked image has a `fileId` to attach. The draft
 * stays until the post succeeds, so a refused post can be fixed and retried;
 * only what was posted is cleared, not what was added while it was in flight.
 */
export function CommentComposer({ onComment, isPosting, onUploadImage, onDiscardImage }: CommentComposerProps) {
	const [value, setValue] = useState('')
	const { images, pickImages, removeImage, postImages } = usePendingImages({ onUploadImage, onDiscardImage })
	const [links, setLinks] = useState<ICommentLink[]>([])
	const [isAddingLink, setIsAddingLink] = useState(false)

	const isUploading = images.some((image) => !image.fileId)
	const isEmpty = !value.trim() && images.length === 0 && links.length === 0

	const submit = (e: FormEvent) => {
		e.preventDefault()
		if (isEmpty || isUploading || isPosting) return
		const content = value.trim()
		const posted = { images, links }
		postImages(
			posted.images,
			Promise.resolve(
				onComment({
					content,
					attachments: images.flatMap((image) => (image.fileId ? [{ fileId: image.fileId }] : [])),
					links,
				}),
			),
		).then(
			() => {
				setValue((current) => (current.trim() === content ? '' : current))
				setLinks((list) => list.filter((link) => !posted.links.includes(link)))
			},
			// The host reports the failure; the draft stays for a retry.
			() => undefined,
		)
	}

	return (
		<form onSubmit={submit} className="mt-4 flex flex-col gap-2 border-t border-ds-border pt-4">
			<textarea
				aria-label="Add a comment"
				value={value}
				onChange={(e) => setValue(e.target.value)}
				placeholder="Add a comment…"
				rows={2}
				className="w-full resize-y rounded-ds-md border border-ds-border bg-ds-surface-elevated px-3 py-2 text-sm text-ds-text placeholder:text-ds-text-subtle focus:border-ds-primary focus:outline-none"
			/>
			{images.length ? (
				<ul aria-label="Images to attach" className="flex flex-wrap gap-2">
					{images.map((image) => (
						<PendingImageTile key={image.key} image={image} onRemove={() => removeImage(image.key)} />
					))}
				</ul>
			) : null}
			{links.length ? (
				<ul aria-label="Links to attach" className="flex flex-wrap gap-2">
					{links.map((link, i) => (
						<li key={`${link.url}-${i}`} className="min-w-0 max-w-full">
							<Chip
								variant="secondary"
								onRemove={() => setLinks((list) => list.filter((_, idx) => idx !== i))}
								removeLabel={`Remove link ${link.label ?? link.url}`}
							>
								{link.label ?? link.url}
							</Chip>
						</li>
					))}
				</ul>
			) : null}
			{isAddingLink ? (
				<LinkForm
					onAdd={(link) => {
						setLinks((list) => [...list, link])
						setIsAddingLink(false)
					}}
					onCancel={() => setIsAddingLink(false)}
				/>
			) : null}
			<div className="flex items-center justify-between gap-2">
				<div className="flex items-center gap-1">
					{onUploadImage && images.length < MAX_COMMENT_ATTACHMENTS ? (
						<label className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'cursor-pointer gap-1.5')}>
							<ImagePlus className="h-4 w-4" />
							Image
							<input type="file" accept={IMAGE_TYPES.join(',')} multiple onChange={pickImages} className="sr-only" />
						</label>
					) : null}
					{isAddingLink || links.length >= MAX_COMMENT_LINKS ? null : (
						<Button size="sm" variant="ghost" className="gap-1.5" onPress={() => setIsAddingLink(true)}>
							<Link2 className="h-4 w-4" />
							Link
						</Button>
					)}
				</div>
				<Button type="submit" size="sm" className="rounded-ds-full" isDisabled={isPosting || isUploading || isEmpty}>
					Comment
				</Button>
			</div>
		</form>
	)
}
